/**
 * Staff (design 2B): list over Auth members (array PageSource — full frozen
 * pager with real totals), visible row actions, no Delete anywhere. Each
 * member's roles show in the list (2026-10-23) so access can be checked at a
 * glance; a member with no role says so.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Machine, Notice } from '@/ui/kit';
import {
  Column,
  MobileOnly,
  Pager,
  PrimaryCell,
  RowButton,
  TableSearch,
  WorkTable,
} from '@/ui/WorkTable';
import { ConfirmDialog } from '@/ui/formkit';
import { useArrayList } from '@/core/api/usePagedList';
import { listMembers, resumeMember, suspendMember, type MemberRow } from '@/core/auth/adminApi';
import { useAuthz } from '@/core/authz/AuthzProvider';

export function memberDisplayName(m: MemberRow): string {
  return m.user.displayName ?? m.user.username ?? m.uid;
}

export function StaffTab() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const authz = useAuthz();
  const clinicId = authz.clinicId ?? '';
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState<MemberRow | null>(null);

  const filter = useCallback(
    (m: MemberRow) => {
      const q = search.trim().toLowerCase();
      if (q === '') return true;
      return [m.user.displayName, m.user.username, m.user.phone, m.user.email]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    },
    [search],
  );

  const list = useArrayList<MemberRow>({
    keyBase: ['members', clinicId],
    fetchAll: () => listMembers(clinicId),
    filter,
    // Members and their roles rarely change in a clinic, and every edit made
    // in the panel (status, roles, a renamed role, a new member) refreshes
    // this list on its own — so it is kept for the working day.
    staleTime: 12 * 60 * 60_000,
    gcTime: 24 * 60 * 60_000,
  });

  const toggle = useMutation({
    mutationFn: (m: MemberRow) =>
      m.status === 'suspended' ? resumeMember(clinicId, m.uid) : suspendMember(clinicId, m.uid),
    onSuccess: () => {
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: ['members', clinicId] });
    },
  });

  const columns: Column[] = useMemo(
    () => [
      { id: 'member', label: t('staff.colMember'), width: '1.7fr' },
      { id: 'roles', label: t('staff.colRoles'), width: '1.4fr', tier: 'tablet-up' },
      { id: 'status', label: t('staff.colStatus'), width: 'auto', tier: 'tablet-up' },
      { id: 'actions', label: t('staff.colActions'), width: 'auto', align: 'end' },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl.locale],
  );

  const roleChips = (m: MemberRow) =>
    m.admin || m.roles.length > 0 ? (
      <>
        {m.admin && <Chip kind="warn">{t('staff.admin')}</Chip>}
        {m.roles.map((r) => (
          <Chip key={r.id} kind="info">
            <span dir="auto">{r.name}</span>
          </Chip>
        ))}
      </>
    ) : (
      <Chip kind="quiet">{t('staff.noRole')}</Chip>
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <p className={a.pageSub} style={{ margin: 0 }}>
          {t('staff.sub')}
        </p>
        <Link to="/clinic-setup/staff/new">
          <Button variant="primary">{t('staff.add')}</Button>
        </Link>
      </div>

      <WorkTable<MemberRow>
        aria-label={t('setup.tabStaff') as string}
        columns={columns}
        rows={list.view.rows}
        rowKey={(m) => m.uid}
        loading={list.loading}
        error={list.error !== null}
        onRetry={list.refetch}
        toolbar={
          <TableSearch
            value={search}
            onChange={setSearch}
            placeholder={t('staff.searchPh') as string}
          />
        }
        empty={{
          title: t(search ? 'table.noResultsTitle' : 'staff.emptyTitle') as string,
          body: t(search ? 'table.noResultsBody' : 'staff.emptyBody') as string,
        }}
        renderCell={(m, col) => {
          switch (col) {
            case 'member':
              return (
                <>
                  <PrimaryCell
                    avatar={memberDisplayName(m).slice(0, 2)}
                    main={memberDisplayName(m)}
                    sub={m.user.phone ? <Machine>{m.user.phone}</Machine> : (m.user.email ?? '')}
                  />
                  {/* The Roles column drops out on phones; the roles move in here. */}
                  <MobileOnly>{roleChips(m)}</MobileOnly>
                </>
              );
            case 'roles':
              return (
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{roleChips(m)}</span>
              );
            case 'status':
              return m.status === 'suspended' ? (
                <Chip kind="quiet">{t('staff.suspended')}</Chip>
              ) : (
                <Chip kind="ok">{t('staff.active')}</Chip>
              );
            case 'actions':
              return (
                <span style={{ display: 'flex', gap: 8 }}>
                  <Link to="/clinic-setup/staff/$uid" params={{ uid: m.uid }}>
                    <RowButton>{t('common.view')}</RowButton>
                  </Link>
                  <RowButton danger={m.status !== 'suspended'} onClick={() => setConfirm(m)}>
                    {m.status === 'suspended' ? t('staff.reactivate') : t('staff.deactivate')}
                  </RowButton>
                </span>
              );
            default:
              return null;
          }
        }}
        footer={
          <Pager
            view={list.view}
            perPage={list.perPage}
            onPerPage={list.setPerPage}
            onPage={list.goTo}
          />
        }
      />

      <Notice kind="info">{t('staff.noDelete')}</Notice>

      <ConfirmDialog
        open={confirm !== null}
        title={
          confirm
            ? (t(
                confirm.status === 'suspended' ? 'staff.reactivateTitle' : 'staff.deactivateTitle',
                {
                  name: memberDisplayName(confirm),
                },
              ) as string)
            : ''
        }
        body={
          confirm
            ? t(confirm.status === 'suspended' ? 'staff.reactivateBody' : 'staff.deactivateBody')
            : ''
        }
        effectTitle={
          confirm?.status !== 'suspended' ? (t('staff.deactivateEffectTitle') as string) : undefined
        }
        effect={confirm?.status !== 'suspended' ? t('staff.deactivateEffect') : undefined}
        confirmLabel={
          confirm
            ? (t(
                confirm.status === 'suspended' ? 'staff.reactivate' : 'staff.deactivate',
              ) as string)
            : ''
        }
        danger={confirm?.status !== 'suspended'}
        busy={toggle.isPending}
        onConfirm={() => confirm && toggle.mutate(confirm)}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
}
